import { Button } from '@/components/ui/button';
import { IconTrash } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import Modal from '@/components/ui/modal';
import { Pen } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';

interface ExcludedCountryItemProps {
    name: string;
    id: string;
    parentModalId: string;
    onRename: (newName: string) => Promise<void>;
    onDelete: (id: string) => void;
    onApply: () => void;
}

export default function ExcludedCountryItem({
    name,
    id,
    parentModalId,
    onRename,
    onDelete,
    onApply,
}: ExcludedCountryItemProps) {
    const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
    const [applyModalOpen, setApplyModalOpen] = useState<boolean>(false);
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const t = useTranslations();

    const inputName = useRef<HTMLInputElement>(null);

    const openRenameModal = () => {
        setRenameModalOpen(true);
    };

    const renameTemplate = async () => {
        if (inputName.current?.value.trim()) {
            setIsRenaming(true);
            try {
                await onRename(inputName.current.value);
                setRenameModalOpen(false);
            } catch (error) {
                void error;
            } finally {
                setIsRenaming(false);
            }
        }
    };

    const deleteTemplate = async () => {
        setIsDeleting(true);
        try {
            await onDelete(id);
            setConfirmDeleteOpen(false);
        } catch (error: unknown) {
            void error;
            setConfirmDeleteOpen(false);
        } finally {
            setIsDeleting(false);
            setConfirmDeleteOpen(false);
        }
    };

    const closeBtnPostion: string = 'top-[20px] right-[20px]';

    return (
        <div>
            <div
                onClickCapture={() => setApplyModalOpen(true)}
                className={`
                    flex mb-[10px] p-[24px] py-[0] h-[72px]
                    items-center justify-center text-center
                    text-[14px] font-[500]
                    border-1 border-solid border-white
                    rounded-[8px]
                    hover:bg-[#DD06AE] hover:border-[#DD06AE]
                    transition-all duration-300 cursor-pointer
                `}
            >
                <span className="max-w-[170px] line-clamp-2">{name}</span>
            </div>
            <div className="flex justify-between">
                <span className="flex items-center cursor-pointer gap-[5px]" onClickCapture={() => openRenameModal()}>
                    <Pen />
                    <span className="underline text-[12px]">
                        {t('ScreeningRoom.modal.excludeTemplate.renameLabel')}
                    </span>
                </span>
                <span
                    className="flex items-center cursor-pointer gap-[5px]"
                    onClickCapture={() => setConfirmDeleteOpen(true)}
                >
                    <IconTrash />
                    <span className="underline text-[12px]">
                        {t('ScreeningRoom.modal.excludeTemplate.deleteLabel')}
                    </span>
                </span>
            </div>

            <Modal
                parentModalId={parentModalId}
                ModalSize="max-w-[545px]"
                isOpen={renameModalOpen}
                onClose={() => !isRenaming && setRenameModalOpen(false)}
                closePosition={closeBtnPostion}
            >
                <div className="text-[24px] font-[700] mb-[25px] mt-[20px]">
                    {t('ScreeningRoom.modal.excludeTemplate.renameTitle')}
                </div>
                <Input
                    ref={inputName}
                    className="w-full max-w-[300px] px-[20px] py-[22px] m-auto border-white border-1 border-[rgba(255,255,255,.80)]"
                    placeholder={t('ScreeningRoom.modal.excludeTemplate.newTemplatePlaceholder')}
                    defaultValue={name}
                    disabled={isRenaming}
                />
                <Button
                    className={`hover:bg-[#B10DCE] cursor-pointer px-[30px] py-[25px] text-[14px] rounded-[8px] mt-[30px] mb-[20px] ${
                        isRenaming ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={renameTemplate}
                    disabled={isRenaming}
                >
                    {t('ScreeningRoom.modal.excludeTemplate.renameButtonLabel')}
                </Button>
            </Modal>

            <Modal
                parentModalId={parentModalId}
                ModalSize="max-w-[545px]"
                isOpen={confirmDeleteOpen}
                onClose={() => {
                    setConfirmDeleteOpen(false);
                }}
                closePosition={closeBtnPostion}
            >
                <div className="text-[24px] font-[700] mt-[20px]">
                    {t('ScreeningRoom.modal.excludeTemplate.deleteTitle')}
                </div>
                <div className="flex gap-3 justify-center">
                    <Button
                        className="cursor-pointer bg-[#0E0E0E] px-[30px] py-[25px] text-[14px] rounded-[12px] mt-[30px] mb-[20px] border-[#fff] border-1 hover:bg-[#B10DCE] hover:border-[#B10DCE] text-white transition duration-300"
                        onClick={() => {
                            setConfirmDeleteOpen(false);
                        }}
                    >
                        {t('ScreeningRoom.modal.excludeTemplate.cancelButtonLabel')}
                    </Button>
                    <Button
                        className={`cursor-pointer hover:bg-[#B10DCE] px-[30px] py-[25px] text-[14px] rounded-[8px] mt-[30px] mb-[20px] ${
                            isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={async () => {
                            setConfirmDeleteOpen(false);
                            await deleteTemplate();
                        }}
                        disabled={isDeleting}
                    >
                        {t('ScreeningRoom.modal.excludeTemplate.deleteButtonLabel')}
                    </Button>
                </div>
            </Modal>

            <Modal
                closePosition={closeBtnPostion}
                parentModalId={parentModalId}
                ModalSize="max-w-[545px]"
                isOpen={applyModalOpen}
                onClose={() => {
                    setApplyModalOpen(false);
                }}
            >
                <div className="text-[24px] font-[700] mt-[20px]">
                    {t('ScreeningRoom.modal.excludeTemplate.applyTitle')}
                </div>
                <div className="flex gap-3 justify-center">
                    <Button
                        className="cursor-pointer hover:bg-[#B10DCE] px-[30px] py-[25px] text-[14px] rounded-[8px] mt-[30px] mb-[20px]"
                        onClick={() => {
                            setApplyModalOpen(false);
                        }}
                    >
                        {t('ScreeningRoom.modal.excludeTemplate.applyCancel')}
                    </Button>
                    <Button
                        className="cursor-pointer hover:bg-[#B10DCE] px-[30px] py-[25px] text-[14px] rounded-[8px] mt-[30px] mb-[20px]"
                        onClick={() => {
                            onApply();
                            setApplyModalOpen(false);
                        }}
                    >
                        {t('ScreeningRoom.modal.excludeTemplate.applyConfirm')}
                    </Button>
                </div>
            </Modal>
        </div>
    );
}
